import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type LatestDoc = {
  doc_type: "terms" | "privacy";
  version: string;
  title: string;
  content_md: string;
};

// Updated policy: require acceptance of the *current* version of each legal
// document (Terms + Privacy). If the user has not accepted the current
// version for any doc_type, they are redirected to /terms to re-accept.
// Fail-open on errors or if no docs configured (do not lock users out).
export function useLegalAcceptanceCheck() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [latestDocs, setLatestDocs] = useState<LatestDoc[]>([]);

  const check = useCallback(async () => {
    if (!user?.id) {
      setNeedsAcceptance(false);
      setLatestDocs([]);
      setLoading(false);
      return;
    }

    // Ensure we show the loading state while verifying acceptance
    setLoading(true);

    try {
      // Fetch current (is_current) docs for display purposes only.
      const { data: docs, error: docsErr } = await supabase
        .from("legal_documents")
        .select("doc_type, version, title, content_md")
        .eq("is_current", true);
      if (docsErr) throw docsErr;

      if (!docs || docs.length === 0) {
        // Nothing configured -> do not block.
        setNeedsAcceptance(false);
        setLatestDocs([]);
        return;
      }
      setLatestDocs(docs as LatestDoc[]);

      // Fetch the user's accepted versions to compare against current docs.
      const { data: acceptRows, error: acceptErr } = await supabase
        .from("user_legal_acceptances")
        .select("doc_type, version")
        .eq("user_id", user.id)
        .in("doc_type", ["terms", "privacy"]);
      if (acceptErr) throw acceptErr;

      // Require acceptance of the *current* version of each legal document.
      // This ensures that when we publish a new Terms/Privacy version, existing
      // users are required to re-accept the latest terms before continuing.
      const hasAcceptedLatest = (docs || []).every((doc) =>
        (acceptRows || []).some(
          (row) => row.doc_type === doc.doc_type && row.version === doc.version
        )
      );

      setNeedsAcceptance(!hasAcceptedLatest);
    } catch {
      // Fail-closed on error: block access when consent cannot be verified
      setNeedsAcceptance(true);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    check();
  }, [check]);

  return { loading, needsAcceptance, latestDocs, recheck: check };
}
